import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFindMany, useGlobalAction, useFindOne, useUser, useSession, useAction } from '@gadgetinc/react';
import { marked } from 'marked';
import { api } from '../api';
import { devLog, devError, devWarn } from '../lib/devUtils';

// Safely import print/save dependencies with graceful degradation
let jsPDF, html2canvas;
let printSaveFeaturesAvailable = false;

try {
  const jsPDFModule = require('jspdf');
  jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
  html2canvas = require('html2canvas');
  printSaveFeaturesAvailable = !!(jsPDF && html2canvas);
} catch (error) {
  devWarn('Print/Save features not available:', error);
  printSaveFeaturesAvailable = false;
}

export default function ChatInterface({ assistantId: propAssistantId, threadId: propThreadId, greeting, onClose } = {}) {
  const { assistantId: urlAssistantId } = useParams();
  const assistantId = urlAssistantId || propAssistantId;
  const navigate = useNavigate();
  const user = useUser(api);
  const session = useSession(api);
  const [message, setMessage] = useState('');
  const [currentThreadId, setCurrentThreadId] = useState(propThreadId || null);
  const [isTyping, setIsTyping] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showThreadModal, setShowThreadModal] = useState(false);
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [selectedThreadForResume, setSelectedThreadForResume] = useState(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  const [threadReady, setThreadReady] = useState(false);
  const [modalDecisionMade, setModalDecisionMade] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [newMessageAnimation, setNewMessageAnimation] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [isStuckToBottom, setIsStuckToBottom] = useState(true);
  const [newMessagesWhileScrolled, setNewMessagesWhileScrolled] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const isScrollingProgrammaticallyRef = useRef(false);
  const [flaggingMessageId, setFlaggingMessageId] = useState(null);
  const [flagComments, setFlagComments] = useState({});
  const [flagReasons, setFlagReasons] = useState({});
  const [savingFlags, setSavingFlags] = useState({});
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [printSaveAvailable, setPrintSaveAvailable] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSavingPDF, setIsSavingPDF] = useState(false);
  const [copyingMessageId, setCopyingMessageId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [threadSummaries, setThreadSummaries] = useState({});
  const [loadingSummaries, setLoadingSummaries] = useState({});
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const timeoutRef = useRef(null);
  const refetchTimeoutRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Enhanced message content rendering to handle various JSON structures
  const renderMessageContent = (content) => {
    if (!content) return '';
    
    if (typeof content === 'string') {
      return content;
    }
    
    if (typeof content === 'object') {
      // Handle the specific format with role and content properties (from our API responses)
      if (content.role && content.content) {
        // If content is a string, return it directly
        if (typeof content.content === 'string') {
          return content.content;
        }
        // If content is an array (OpenAI format), extract text
        if (Array.isArray(content.content)) {
          const textItem = content.content.find(item => item.type === 'text');
          if (textItem && textItem.text) {
            if (typeof textItem.text === 'string') {
              return textItem.text;
            }
            if (textItem.text.value) {
              return textItem.text.value;
            }
          }
        }
        // If content is an object, try to extract text value
        if (typeof content.content === 'object' && content.content.value) {
          return String(content.content.value);
        }
      }
      
      // Handle OpenAI message format with content array (direct format)
      if (content.content && Array.isArray(content.content)) {
        const textItem = content.content.find(item => item.type === 'text');
        if (textItem && textItem.text) {
          if (typeof textItem.text === 'string') {
            return textItem.text;
          }
          if (textItem.text.value) {
            return textItem.text.value;
          }
        }
      }
      
      // Handle direct text property
      if (content.text) {
        if (typeof content.text === 'string') {
          return content.text;
        }
        if (content.text.value) {
          return content.text.value;
        }
      }
      
      // Handle direct value property
      if (content.value) {
        return String(content.value);
      }
      
      // Handle direct content property (simple string content)
      if (content.content && typeof content.content === 'string') {
        return content.content;
      }
      
      // Handle message objects that might have been stringified and parsed
      if (content.message) {
        return renderMessageContent(content.message);
      }
      
      // If it's an object but doesn't match known patterns, try to find any text content
      const possibleTextFields = ['text', 'content', 'message', 'value', 'data'];
      for (const field of possibleTextFields) {
        if (content[field] && typeof content[field] === 'string' && content[field].trim()) {
          return content[field];
        }
      }
      
      // Last resort: if object has only a few properties and one looks like text content
      const keys = Object.keys(content);
      if (keys.length <= 3) { // Avoid stringifying large objects
        // Filter out metadata fields
        const contentKeys = keys.filter(key => 
          !['role', 'openAiMessageId', 'id', 'createdAt', 'updatedAt', 'threadId', 'userId'].includes(key)
        );
        if (contentKeys.length === 1) {
          const singleValue = content[contentKeys[0]];
          if (typeof singleValue === 'string') {
            return singleValue;
          }
        }
      }
      
      // Fallback to string conversion only for small objects to avoid displaying large JSON
      const jsonString = JSON.stringify(content);
      if (jsonString.length < 200) {
        return jsonString;
      } else {
        return '[Complex message content - unable to display]';
      }
    }
    
    return String(content);
  };

  // Fetch assistant details
  const [{ data: assistant, error: assistantError, fetching: assistantFetching }] = useFindOne(
    api.assistant, 
    assistantId, 
    {
      select: { 
        id: true, 
        name: true, 
        description: true,
        allowFileUploads: true,
        acceptedFileTypes: true,
        maxFileSize: true,
        allowedFileCategories: true,
        processFilesWithVision: true,
        fileUploadInstructions: { markdown: true }
      }
    }
  );

  // Fetch user's threads for this assistant (last 3 threads)
  const [{ data: threads, error: threadsError, fetching: threadsFetching }] = useFindMany(api.thread, {
    filter: { 
      AND: [
        { assistantId: { equals: assistantId } },
        { userId: { equals: user?.id } }
      ]
    },
    sort: { updatedAt: 'Descending' },
    first: 3,
    select: { 
      id: true, 
      openAiId: true, 
      description: true, 
      createdAt: true,
      updatedAt: true,
      messages: {
        edges: {
          node: {
            id: true,
            userMessage: true,
            assistantMessage: true,
            contentType: true,
            createdAt: true
          }
        }
      }
    }
  });

  // Fetch messages for current thread
  const [{ data: messages, fetching: messagesFetching, error: messagesError }, refetchMessages] = useFindMany(api.message, {
    filter: { threadId: { equals: currentThreadId } },
    sort: { createdAt: 'Ascending' },
    first: 250,
    select: {
      id: true,
      userMessage: true,
      assistantMessage: true,
      contentType: true,
      createdAt: true,
      userId: true
    },
    skip: !currentThreadId
  });

  // Global actions
  const [{ fetching: sendingMessage, error: sendError }, sendMessage] = useGlobalAction(api.globalCreateMessage);
  const [{ fetching: creatingThread, error: threadError }, createThread] = useGlobalAction(api.globalCreateThread);
  const [{ fetching: initializingThread, error: initError }, initializeThreadWithGreeting] = useGlobalAction(api.initializeThreadWithGreeting);
  const [{ fetching: generatingThreadSummary, error: summaryError }, generateThreadSummary] = useGlobalAction(api.generateThreadSummary);
  
  // Message flagging action
  const [{ fetching: updatingMessage, error: updateError }, updateMessage] = useAction(api.message.update);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const screenWidth = window.innerWidth;
      setIsMobile(mobileRegex.test(userAgent) || screenWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enhanced scroll management with improved velcro effect
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollDebounceTimer = null;

    const handleScroll = () => {
      // Skip processing if this is a programmatic scroll
      if (isScrollingProgrammaticallyRef.current) {
        return;
      }
      
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < 50; // Reduced threshold for better UX
      const isAtBottom = distanceFromBottom < 5; // Stricter bottom detection
      
      // Always allow free scrolling - don't restrict based on user-initiated detection
      setScrollPosition(scrollTop);
      setLastScrollTop(scrollTop);
      
      // Velcro effect logic - only stick when truly at bottom
      if (isAtBottom) {
        // User reached bottom - stick to bottom and reset counters
        setIsStuckToBottom(true);
        setNewMessagesWhileScrolled(0);
        setShowJumpToBottom(false);
        setShowScrollToBottom(false);
        setNewMessageAnimation(false);
      } else {
        // User scrolled away from bottom - always break velcro to allow free scrolling
        setIsStuckToBottom(false);
      }
      
      // Show jump button when scrolled up and there's content to scroll to
      const shouldShowJump = distanceFromBottom > 100 && scrollHeight > clientHeight;
      setShowJumpToBottom(shouldShowJump);
      setShowScrollToBottom(shouldShowJump);
      
      // Clear existing debounce timer
      if (scrollDebounceTimer) {
        clearTimeout(scrollDebounceTimer);
      }
      
      // Track user scrolling state with debounce
      setIsUserScrolling(true);
      scrollDebounceTimer = setTimeout(() => {
        setIsUserScrolling(false);
      }, 150); // Reduced debounce time for more responsive behavior
    };

    // Use passive listener for better performance
    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollDebounceTimer) {
        clearTimeout(scrollDebounceTimer);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [lastScrollTop]); // Keep dependency but don't use lastScrollTop for restrictive logic
  // Enhanced message count tracking for velcro effect
  useEffect(() => {
    const allMessages = [...(messages || []), ...optimisticMessages];
    const filteredMessages = allMessages.filter(msg => {
      // Filter out initialization messages
      if (msg.userMessage) {
        const content = renderMessageContent(msg.userMessage);
        return content !== "Hello, I'm an EMS student seeking medical guidance. How can you help me?";
      }
      return true;
    });

    const currentMessageCount = filteredMessages.length;
    
    if (currentMessageCount > lastMessageCount && lastMessageCount > 0) {
      // New message arrived
      if (!isStuckToBottom && showJumpToBottom) {
        // User is scrolled up - increment new message counter and show animation
        setNewMessagesWhileScrolled(prev => prev + 1);
        setNewMessageAnimation(true);
        setTimeout(() => setNewMessageAnimation(false), 3000);
      }
    }
    
    setLastMessageCount(currentMessageCount);
  }, [messages, optimisticMessages, lastMessageCount, isStuckToBottom, showJumpToBottom, renderMessageContent]);

  // Handle thread initialization - simplified logic
  useEffect(() => {
    const initializeThread = async () => {
      // If we have a prop thread ID, use it directly
      if (propThreadId && !currentThreadId && !threadReady) {
        setCurrentThreadId(propThreadId);
        setThreadReady(true);
        setModalDecisionMade(true);
        return;
      }

      // If already have a thread ready or still loading required data, don't proceed
      if (threadReady || threadsFetching || isCreatingThread || !assistant || !user || threads === null) {
        return;
      }

      // If there are existing threads, show modal for user to choose (only if decision hasn't been made)
      if (threads && threads.length > 0 && !showThreadModal && !currentThreadId && !modalDecisionMade) {
        setShowThreadModal(true);
        setModalDecisionMade(true);
        return;
      }

      // If no existing threads, immediately create a new thread with initialization message
      if (threads && threads.length === 0 && !currentThreadId && !isCreatingThread && !modalDecisionMade) {
        setModalDecisionMade(true);
        setIsCreatingThread(true);
        try {
          devLog('Initializing new thread with greeting for assistantId:', assistantId, 'userId:', user.id);
          const result = await initializeThreadWithGreeting({
            assistantId: assistantId,
            userId: user.id
          });
          devLog('initializeThreadWithGreeting result:', result);
          devLog('Full result structure:', JSON.stringify(result, null, 2));
          
          // Check for both possible response structures
          const responseData = result?.data || result;
          const threadData = responseData?.result || responseData;
          
          if (responseData?.success && threadData?.thread?.id) {
            devLog('Setting currentThreadId to:', threadData.thread.id);
            setCurrentThreadId(threadData.thread.id);
            setThreadReady(true);
          } else {
            devError('Invalid response structure from initializeThreadWithGreeting:', {
              result,
              responseData,
              threadData,
              hasSuccess: !!responseData?.success,
              hasThread: !!threadData?.thread,
              hasThreadId: !!threadData?.thread?.id
            });
          }
        } catch (error) {
          devError('Error initializing thread with greeting:', error);
          // Show user-friendly error message
          alert('Failed to create conversation thread. Please try refreshing the page or contact support if the issue persists.');
        } finally {
          setIsCreatingThread(false);
        }
      }
    };

    initializeThread();
  }, [assistant, user, currentThreadId, propThreadId, threads, threadsFetching, assistantId, createThread, sendMessage, threadReady, showThreadModal, isCreatingThread, modalDecisionMade]);

  // Handle thread selection functions
  const handleResumeThread = (threadId = null) => {
    const targetThreadId = threadId || selectedThreadForResume || (threads && threads.length > 0 ? threads[0].id : null);
    if (targetThreadId) {
      setCurrentThreadId(targetThreadId);
      setSelectedThreadForResume(targetThreadId);
      setThreadReady(true);
    }
    setShowThreadModal(false);
    setModalDecisionMade(true);
  };



  const handleStartNewThread = async () => {
    setShowThreadModal(false);
    setModalDecisionMade(true);
    
    if (!isCreatingThread) {
      setIsCreatingThread(true);
      try {
        devLog('Starting new thread for assistantId:', assistantId, 'userId:', user.id);
        const result = await initializeThreadWithGreeting({
          assistantId: assistantId,
          userId: user.id
        });
        devLog('handleStartNewThread - initializeThreadWithGreeting result:', result);
        devLog('handleStartNewThread - Full result structure:', JSON.stringify(result, null, 2));
        
        // Check for both possible response structures
        const responseData = result?.data || result;
        const threadData = responseData?.result || responseData;
        
        if (responseData?.success && threadData?.thread?.id) {
          devLog('handleStartNewThread - Setting currentThreadId to:', threadData.thread.id);
          setCurrentThreadId(threadData.thread.id);
          setThreadReady(true);
        } else {
          devError('handleStartNewThread - Invalid response structure:', {
            result,
            responseData,
            threadData,
            hasSuccess: !!responseData?.success,
            hasThread: !!threadData?.thread,
            hasThreadId: !!threadData?.thread?.id
          });
          // Show error to user
          alert('Failed to create new conversation thread. Please try again.');
        }
      } catch (error) {
        devError('Error initializing thread with greeting in handleStartNewThread:', error);
        // Show more specific error for User role issues
        let errorMessage = 'Error creating new conversation: ';
        if (error.message && (error.message.includes('permission') || error.message.includes('access') || error.message.includes('denied'))) {
          errorMessage += 'Access denied. Please ensure you have permission to use this assistant or contact your administrator.';
        } else if (error.message && error.message.includes('User')) {
          errorMessage += 'User authentication issue. Please try signing out and signing back in.';
        } else {
          errorMessage += (error.message || 'Unknown error. Please try refreshing the page.');
        }
        alert(errorMessage);
      } finally {
        setIsCreatingThread(false);
      }
    }
  };

  const handleCancelModal = () => {
    setShowThreadModal(false);
    setModalDecisionMade(true);
    
    // Auto-create a new thread when modal is cancelled
    if (!isCreatingThread && assistant && user) {
      setIsCreatingThread(true);
      devLog('handleCancelModal - Creating new thread for assistantId:', assistantId, 'userId:', user.id);
      initializeThreadWithGreeting({
        assistantId: assistantId,
        userId: user.id
      }).then(result => {
        devLog('handleCancelModal - initializeThreadWithGreeting result:', result);
        devLog('handleCancelModal - Full result structure:', JSON.stringify(result, null, 2));
        
        // Check for both possible response structures
        const responseData = result?.data || result;
        const threadData = responseData?.result || responseData;
        
        if (responseData?.success && threadData?.thread?.id) {
          devLog('handleCancelModal - Setting currentThreadId to:', threadData.thread.id);
          setCurrentThreadId(threadData.thread.id);
          setThreadReady(true);
        } else {
          devError('handleCancelModal - Invalid response structure:', {
            result,
            responseData,
            threadData,
            hasSuccess: !!responseData?.success,
            hasThread: !!threadData?.thread,
            hasThreadId: !!threadData?.thread?.id
          });
          // Show error to user
          alert('Failed to create conversation thread. Please try again.');
        }
      }).catch(error => {
        devError('Error initializing thread with greeting in handleCancelModal:', error);
        // Show more specific error for User role issues
        let errorMessage = 'Error creating conversation: ';
        if (error.message && (error.message.includes('permission') || error.message.includes('access') || error.message.includes('denied'))) {
          errorMessage += 'Access denied. Please ensure you have permission to use this assistant or contact your administrator.';
        } else if (error.message && error.message.includes('User')) {
          errorMessage += 'User authentication issue. Please try signing out and signing back in.';
        } else {
          errorMessage += (error.message || 'Unknown error. Please try refreshing the page.');
        }
        alert(errorMessage);
      }).finally(() => {
        setIsCreatingThread(false);
      });
    }
  };

  // Helper function to format time ago
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;
    return `${Math.floor(diffMonths / 12)}y ago`;
  };

  // Helper function to get thread message count
  const getThreadMessageCount = (thread) => {
    return thread.messages?.edges?.length || 0;
  };

  // Helper function to get last activity time
  const getLastActivityTime = (thread) => {
    const messages = thread.messages?.edges || [];
    if (messages.length === 0) {
      return thread.createdAt;
    }
    
    // Get the most recent message
    const lastMessage = messages.reduce((latest, current) => {
      const currentTime = new Date(current.node.createdAt);
      const latestTime = new Date(latest.node.createdAt);
      return currentTime > latestTime ? current : latest;
    }, messages[0]);
    
    return lastMessage.node.createdAt;
  };

  // Helper function to get thread status
  const getThreadStatus = (thread) => {
    const messageCount = getThreadMessageCount(thread);
    const lastActivity = new Date(getLastActivityTime(thread));
    const now = new Date();
    const daysSinceActivity = (now - lastActivity) / (1000 * 60 * 60 * 24);
    
    if (messageCount === 0) return 'empty';
    if (daysSinceActivity < 1) return 'active';
    if (daysSinceActivity < 7) return 'recent';
    if (daysSinceActivity < 30) return 'inactive';
    return 'archived';
  };

  // Helper function to group threads by recency
  const groupThreadsByRecency = (threads) => {
    if (!threads || threads.length === 0) return {};
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const groups = {
      today: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };
    
    threads.forEach(thread => {
      const lastActivity = new Date(getLastActivityTime(thread));
      
      if (lastActivity >= today) {
        groups.today.push(thread);
      } else if (lastActivity >= weekAgo) {
        groups.thisWeek.push(thread);
      } else if (lastActivity >= monthAgo) {
        groups.thisMonth.push(thread);
      } else {
        groups.older.push(thread);
      }
    });
    
    return groups;
  };

  // Enhanced async function to generate thread summaries with caching
  const generateThreadSummaryAsync = useCallback(async (thread) => {
    if (!thread.id || threadSummaries[thread.id] || loadingSummaries[thread.id]) {
      return;
    }
    
    // Skip summary generation for threads with few messages
    const messageCount = thread.messages?.edges?.length || 0;
    if (messageCount <= 3) {
      return;
    }
    
    // Skip summary generation if thread already has an adequate description (>10 characters)
    if (thread.description && thread.description.length > 10) {
      return;
    }
    
    setLoadingSummaries(prev => ({ ...prev, [thread.id]: true }));
    
    try {
      const summaryResult = await generateThreadSummary({
        threadId: thread.id
      });
      
      if (summaryResult?.result?.summary) {
        setThreadSummaries(prev => ({
          ...prev,
          [thread.id]: summaryResult.result.summary
        }));
      } else if (summaryResult?.data?.result?.summary) {
        setThreadSummaries(prev => ({
          ...prev,
          [thread.id]: summaryResult.data.result.summary
        }));
      }
    } catch (error) {
      devWarn('Failed to generate thread summary:', error);
      
      // Check if it's a rate limit error and don't retry immediately
      if (error.message && (error.message.includes('rate limit') || error.message.includes('429'))) {
        // Mark this thread as processed to prevent immediate retries
        setThreadSummaries(prev => ({
          ...prev,
          [thread.id]: '[Rate limited - try again later]'
        }));
      }
    } finally {
      setLoadingSummaries(prev => {
        const newState = { ...prev };
        delete newState[thread.id];
        return newState;
      });
    }
  }, [generateThreadSummary, threadSummaries, loadingSummaries]);

  // Enhanced function to get thread description with AI summaries
  const getThreadDescriptionSync = useCallback((thread) => {
    // Return cached AI summary if available
    if (threadSummaries[thread.id]) {
      return threadSummaries[thread.id];
    }
    
    // Show loading state if summary is being generated
    if (loadingSummaries[thread.id]) {
      return 'Generating summary...';
    }
    
    // Use existing description if available
    if (thread.description) {
      return thread.description;
    }
    
    // Count total messages in thread
    const messageCount = thread.messages?.edges?.length || 0;
    
    // Get first and last user messages for better context
    const messages = thread.messages?.edges || [];
    const userMessages = messages.filter(edge => edge.node.userMessage);
    
    if (userMessages.length > 0) {
      const firstUserMessage = userMessages[0].node;
      const lastUserMessage = userMessages[userMessages.length - 1].node;
      
      let description = '';
      
      if (typeof firstUserMessage.userMessage === 'string') {
        description = firstUserMessage.userMessage;
      } else if (firstUserMessage.userMessage && typeof firstUserMessage.userMessage === 'object') {
        description = JSON.stringify(firstUserMessage.userMessage);
      }
      
      // If more than one user message and it's different from first, show evolution
      if (userMessages.length > 1 && lastUserMessage.id !== firstUserMessage.id) {
        let lastDescription = '';
        if (typeof lastUserMessage.userMessage === 'string') {
          lastDescription = lastUserMessage.userMessage;
        } else if (lastUserMessage.userMessage && typeof lastUserMessage.userMessage === 'object') {
          lastDescription = JSON.stringify(lastUserMessage.userMessage);
        }
        
        // If last message is very different from first, show progression
        if (lastDescription && lastDescription !== description) {
          const shortFirst = description.length > 30 ? description.substring(0, 30) + '...' : description;
          const shortLast = lastDescription.length > 30 ? lastDescription.substring(0, 30) + '...' : lastDescription;
          return `${shortFirst} → ${shortLast}`;
        }
      }
      
      // Truncate to reasonable length
      return description.length > 60 ? description.substring(0, 60) + '...' : description;
    }
    
    // Fallback to creation date
    const date = new Date(thread.createdAt);
    return `Conversation from ${date.toLocaleDateString()}`;
  }, [threadSummaries, loadingSummaries]);

  // Helper function to get context-aware placeholder text
  const getPlaceholderText = () => {
    if (!threadReady) {
      return "Preparing conversation...";
    }
    
    if (!assistant) {
      return "Type your message...";
    }
    
    // Check if this is an administrative assistant
    const isAdministrative = 
      assistantId === '22' || 
      (assistant.name && assistant.name.toLowerCase().includes('administrative')) ||
      (assistant.description && assistant.description.toLowerCase().includes('administrative'));
    
    if (isAdministrative) {
      return "Ask about administrative tasks, user management, or educational programs...";
    }
    
    // Default to medical context
    return "Ask a medical question...";
  };

  // Enhanced auto-scroll with velcro effect
  useEffect(() => {
    // Auto-scroll only when stuck to bottom or not currently user scrolling
    if (isStuckToBottom && !isUserScrolling) {
      // Use a small delay to ensure DOM is updated
      const scrollTimer = setTimeout(() => {
        scrollToBottom();
      }, 50);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [messages, optimisticMessages, isStuckToBottom, isUserScrolling]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
    };
  }, []);

  // Handle send message loading state
  useEffect(() => {
    setIsTyping(sendingMessage || initializingThread);
  }, [sendingMessage, initializingThread]);

  // Enhanced focus management for textarea
  useEffect(() => {
    // Focus textarea when messages change (new messages received)
    if (textareaRef.current && messages && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [messages]);

  // Focus textarea when typing indicator stops (assistant finished responding)
  useEffect(() => {
    if (textareaRef.current && !isTyping && threadReady) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isTyping, threadReady]);

  // Focus textarea when send operation completes
  useEffect(() => {
    if (textareaRef.current && !sendingMessage && threadReady) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [sendingMessage, threadReady]);

  // Focus textarea when thread becomes ready
  useEffect(() => {
    if (textareaRef.current && threadReady && !isCreatingThread) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 200);
    }
  }, [threadReady, isCreatingThread]);

  // Check print/save permissions and feature availability
  useEffect(() => {
    const checkPrintSaveAvailability = () => {
      try {
        const hasPermission = user?.canPrintSaveChats !== false; // Default to true if undefined
        // Show buttons if user has permission, even if features might have issues
        setPrintSaveAvailable(hasPermission);
      } catch (error) {
        console.warn('Error checking print/save availability:', error);
        setPrintSaveAvailable(false);
      }
    };

    if (user) {
      checkPrintSaveAvailability();
    }
  }, [user]);

  // Focus textarea when optimistic messages are cleared (real messages arrived)
  useEffect(() => {
    if (textareaRef.current && optimisticMessages.length === 0 && threadReady) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 150);
    }
  }, [optimisticMessages, threadReady]);

  // Prevent window focus/blur from resetting the chat
  useEffect(() => {
    const handleWindowFocus = () => {
      // Prevent any reset behavior on window focus and refocus textarea
      if (textareaRef.current && threadReady) {
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      }
    };
    
    const handleWindowBlur = () => {
      // Prevent any reset behavior on window blur
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [threadReady]);

  const scrollToBottom = useCallback((force = false) => {
    if (messagesContainerRef.current && (!isUserScrolling || force)) {
      const container = messagesContainerRef.current;
      
      // Set flag to prevent scroll event handler from interfering
      isScrollingProgrammaticallyRef.current = true;
      
      const scrollOptions = {
        top: container.scrollHeight,
        behavior: 'smooth'
      };
      container.scrollTo(scrollOptions);
      
      // Clear the flag after scroll completes
      setTimeout(() => {
        isScrollingProgrammaticallyRef.current = false;
      }, 600); // Wait for smooth scroll to complete
      
      // Hide jump-to-bottom button immediately when scrolling to bottom
      if (force) {
        setShowJumpToBottom(false);
        setNewMessageAnimation(false);
      }
    }
  }, [isUserScrolling]);

  const scrollToBottomInstant = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Message flagging functions
  const handleFlagMessage = useCallback((messageId) => {
    setFlaggingMessageId(messageId);
    setFlagComments(prev => ({ ...prev, [messageId]: '' }));
    setFlagReasons(prev => ({ ...prev, [messageId]: '' }));
    setShowFlagModal(true);
  }, []);

  const handleCancelFlag = useCallback(() => {
    const messageId = flaggingMessageId;
    setFlaggingMessageId(null);
    setShowFlagModal(false);
    if (messageId) {
      setFlagComments(prev => {
        const newComments = { ...prev };
        delete newComments[messageId];
        return newComments;
      });
      setFlagReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons[messageId];
        return newReasons;
      });
    }
  }, [flaggingMessageId]);

  const handleSaveFlag = useCallback(async () => {
    const messageId = flaggingMessageId;
    if (!messageId) return;
    
    const reason = flagReasons[messageId] || '';
    const comment = flagComments[messageId] || '';
    
    if (!reason) {
      alert('Please select a reason for flagging this message.');
      return;
    }
    
    setSavingFlags(prev => ({ ...prev, [messageId]: true }));
    
    try {
      const flagReason = comment ? `${reason}: ${comment}` : reason;
      
      await updateMessage({
        id: messageId,
        flagReason: flagReason,
        reviewStatus: 'flagged'
      });
      
      // Update local message data
      setFlaggingMessageId(null);
      setShowFlagModal(false);
      setFlagComments(prev => {
        const newComments = { ...prev };
        delete newComments[messageId];
        return newComments;
      });
      setFlagReasons(prev => {
        const newReasons = { ...prev };
        delete newReasons[messageId];
        return newReasons;
      });
      
      // Refresh messages to show updated flag status
      setTimeout(() => {
        refetchMessages();
      }, 500);
      
    } catch (error) {
      devError('Error flagging message:', error);
      alert('Failed to flag message. Please try again.');
    } finally {
      setSavingFlags(prev => ({ ...prev, [messageId]: false }));
    }
  }, [flaggingMessageId, flagComments, flagReasons, updateMessage, refetchMessages]);

  const handleFlagReasonChange = useCallback((messageId, value) => {
    setFlagReasons(prev => ({ ...prev, [messageId]: value }));
  }, []);

  const handleFlagCommentChange = useCallback((messageId, value) => {
    setFlagComments(prev => ({ ...prev, [messageId]: value }));
  }, []);

  // Print and save utility functions
  const checkPrintSavePermission = useCallback(() => {
    try {
      return user?.canPrintSaveChats !== false;
    } catch (error) {
      devWarn('Error checking print permission:', error);
      return true; // Default to allowing if check fails
    }
  }, [user]);

  const showFeatureError = useCallback((feature, error) => {
    const errorMessage = `${feature} feature is temporarily unavailable. ${error?.message || 'Please try again later.'}`;
    devError(`${feature} error:`, error);
    
    // Show non-blocking error message
    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${isDarkMode ? '#dc2626' : '#fee2e2'};
      color: ${isDarkMode ? '#ffffff' : '#dc2626'};
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 9999;
      font-size: 14px;
      max-width: 300px;
    `;
    errorDiv.textContent = errorMessage;
    document.body.appendChild(errorDiv);
    
    // Remove after 5 seconds
    setTimeout(() => {
      if (document.body.contains(errorDiv)) {
        document.body.removeChild(errorDiv);
      }
    }, 5000);
  }, [isDarkMode]);

  const safeCopyToClipboard = useCallback(async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      }
    } catch (error) {
      devError('Failed to copy to clipboard:', error);
      return false;
    }
  }, []);

  // Print functionality
  const handlePrintChat = useCallback(async () => {
    if (!checkPrintSavePermission()) {
      showFeatureError('Print', new Error('You do not have permission to print chats'));
      return;
    }

    setIsPrinting(true);
    
    try {
      // Add print class to body for print-specific styling
      document.body.classList.add('printing-chat');
      
      // Use browser's native print dialog
      window.print();
      
    } catch (error) {
      devError('Print error:', error);
      showFeatureError('Print', error);
    } finally {
      // Remove print class after a delay
      setTimeout(() => {
        document.body.classList.remove('printing-chat');
        setIsPrinting(false);
      }, 1000);
    }
  }, [checkPrintSavePermission, showFeatureError]);

  // PDF save functionality
  const handleSaveChatAsPDF = useCallback(async () => {
    if (!checkPrintSavePermission()) {
      showFeatureError('Save PDF', new Error('You do not have permission to save chats'));
      return;
    }

    if (!printSaveFeaturesAvailable) {
      showFeatureError('Save PDF', new Error('PDF generation libraries not available. Please contact your administrator.'));
      return;
    }

    setIsSavingPDF(true);

    try {
      const chatContainer = messagesContainerRef.current;
      if (!chatContainer) {
        throw new Error('Chat content not found');
      }

      // Create a clean version of the chat for PDF
      const tempContainer = document.createElement('div');
      tempContainer.style.cssText = `
        position: absolute;
        left: -9999px;
        top: -9999px;
        width: 800px;
        background: white;
        color: black;
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.4;
        padding: 20px;
      `;

      // Add header
      const header = document.createElement('div');
      header.innerHTML = `
        <h2 style="margin: 0 0 10px 0; font-size: 16px;">Chat with ${assistant?.name || 'AI Assistant'}</h2>
        <p style="margin: 0 0 20px 0; font-size: 10px; color: #666;">
          Generated on ${new Date().toLocaleString()}<br/>
          User: ${user?.email || 'Unknown'}
        </p>
      `;
      tempContainer.appendChild(header);

      // Add messages
      const allMessages = [...(messages || []), ...optimisticMessages]
        .filter(msg => {
          // Filter out initialization messages
          if (msg.userMessage) {
            const content = renderMessageContent(msg.userMessage);
            return content !== "Hello, I'm an EMS student seeking medical guidance. How can you help me?";
          }
          return true;
        })
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      allMessages.forEach(msg => {
        const messageDiv = document.createElement('div');
        messageDiv.style.cssText = 'margin-bottom: 15px; page-break-inside: avoid;';

        if (msg.userMessage) {
          messageDiv.innerHTML = `
            <div style="background: #e3f2fd; padding: 8px 12px; border-radius: 12px; margin-left: 40px;">
              <strong>You:</strong> ${renderMessageContent(msg.userMessage).replace(/\n/g, '<br/>')}
            </div>
          `;
        }

        if (msg.assistantMessage) {
          const assistantContent = renderMessageContent(msg.assistantMessage);
          messageDiv.innerHTML = `
            <div style="background: #f5f5f5; padding: 8px 12px; border-radius: 12px; margin-right: 40px;">
              <strong>${assistant?.name || 'Assistant'}:</strong> ${assistantContent.replace(/\n/g, '<br/>')}
            </div>
          `;
        }

        tempContainer.appendChild(messageDiv);
      });

      document.body.appendChild(tempContainer);

      // Generate PDF
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      document.body.removeChild(tempContainer);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let position = 10;
      const pageHeight = pdf.internal.pageSize.height - 20;

      if (imgHeight <= pageHeight) {
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, position, imgWidth, imgHeight);
      } else {
        // Handle multiple pages
        let remainingHeight = imgHeight;
        let currentPosition = 0;

        while (remainingHeight > 0) {
          const currentPageHeight = Math.min(remainingHeight, pageHeight);
          
          if (currentPosition > 0) {
            pdf.addPage();
          }

          pdf.addImage(
            canvas.toDataURL('image/png'),
            'PNG',
            10,
            position - currentPosition,
            imgWidth,
            imgHeight
          );

          remainingHeight -= currentPageHeight;
          currentPosition += currentPageHeight;
        }
      }

      // Save the PDF
      const fileName = `chat-${assistant?.name || 'assistant'}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      showFeatureError('Save PDF', error);
    } finally {
      setIsSavingPDF(false);
    }
  }, [checkPrintSavePermission, showFeatureError, printSaveFeaturesAvailable, messages, optimisticMessages, assistant, user, renderMessageContent]);

  // Copy message functionality
  const handleCopyMessage = useCallback(async (messageId, content) => {
    setCopyingMessageId(messageId);
    
    try {
      const plainText = renderMessageContent(content);
      const success = await safeCopyToClipboard(plainText);
      
      if (success) {
        // Show success feedback
        const button = document.querySelector(`[data-copy-id="${messageId}"]`);
        if (button) {
          const originalText = button.textContent;
          button.textContent = '✓';
          button.style.color = '#10b981';
          setTimeout(() => {
            button.textContent = originalText;
            button.style.color = '';
          }, 2000);
        }
      } else {
        throw new Error('Copy operation failed');
      }
    } catch (error) {
      showFeatureError('Copy', error);
    } finally {
      setTimeout(() => setCopyingMessageId(null), 500);
    }
  }, [renderMessageContent, safeCopyToClipboard, showFeatureError]);

  // File upload helper functions
  const validateFileType = useCallback((file) => {
    if (!assistant?.acceptedFileTypes || assistant.acceptedFileTypes.length === 0) {
      // Default accepted types if none specified
      const defaultTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
      return defaultTypes.includes(file.type);
    }
    return assistant.acceptedFileTypes.includes(file.type);
  }, [assistant]);

  const validateFileSize = useCallback((file) => {
    const maxSize = assistant?.maxFileSize || 10 * 1024 * 1024; // Default 10MB
    return file.size <= maxSize;
  }, [assistant]);

  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  const handleFileSelect = useCallback((files) => {
    setFileUploadError('');
    const fileArray = Array.from(files);
    const validFiles = [];
    const errors = [];

    fileArray.forEach(file => {
      if (!validateFileType(file)) {
        errors.push(`${file.name}: File type not supported`);
        return;
      }
      if (!validateFileSize(file)) {
        errors.push(`${file.name}: File size exceeds limit (${formatFileSize(assistant?.maxFileSize || 10 * 1024 * 1024)})`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setFileUploadError(errors.join('; '));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setShowFilePreview(true);
    }
  }, [validateFileType, validateFileSize, formatFileSize, assistant]);

  const removeSelectedFile = useCallback((index) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      if (newFiles.length === 0) {
        setShowFilePreview(false);
      }
      return newFiles;
    });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) {
      setDragActive(true);
    }
  }, [dragActive]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  const resetFileUpload = useCallback(() => {
    setSelectedFiles([]);
    setShowFilePreview(false);
    setFileUploadError('');
    setUploadingFiles(false);
    setDragActive(false);
  }, []);

  const canUploadFiles = useCallback(() => {
    return user?.canPrintSaveChats !== false && assistant?.allowFileUploads === true;
  }, [user, assistant]);

  const handleSendMessage = useCallback(async () => {
    // Check if we have either a message or files to send
    const hasTextMessage = message.trim().length > 0;
    const hasFiles = selectedFiles.length > 0;
    
    if ((!hasTextMessage && !hasFiles) || sendingMessage || !user?.id || !assistantId || !threadReady) {
      return;
    }

    // Validate that we have a current thread ID
    if (!currentThreadId) {
      devError('Cannot send message: currentThreadId is null or undefined', {
        currentThreadId,
        assistantId,
        userId: user?.id,
        isCreatingThread,
        threadReady
      });
      
      // Show specific error to user
      alert('Please wait for the conversation thread to be created before sending a message.');
      return;
    }

    const userMessage = hasTextMessage ? message.trim() : (hasFiles ? '[File upload]' : '');
    const tempMessageId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Validate all required parameters
    const messageParams = {
      threadId: currentThreadId,
      userQuestion: userMessage,
      assistantId: assistantId,
      userId: user.id,
      ...(selectedFiles.length > 0 && { files: selectedFiles })
    };

    // Debug logging
    devLog('Sending message with parameters:', messageParams);
    devLog('Current state:', {
      currentThreadId,
      assistantId,
      userId: user?.id,
      messageLength: userMessage.length,
      isCreatingThread,
      threadReady
    });

    // Validate all required parameters are present and valid
    if (!messageParams.threadId) {
      devError('Missing threadId parameter');
      alert('Error: No conversation thread available. Please try refreshing the page.');
      return;
    }
    if (!messageParams.assistantId) {
      devError('Missing assistantId parameter');
      alert('Error: No assistant selected. Please try refreshing the page.');
      return;
    }
    if (!messageParams.userId) {
      devError('Missing userId parameter');
      alert('Error: User not authenticated. Please try signing in again.');
      return;
    }
    if (!messageParams.userQuestion && selectedFiles.length === 0) {
      devError('Missing userQuestion and no files provided');
      alert('Error: Please provide a message or select files to upload.');
      return;
    }
    
    // Clear any existing retry timeout
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
    }

    // Optimistic update: add user message immediately to UI
    const optimisticUserMessage = {
      id: tempMessageId,
      userMessage: userMessage,
      assistantMessage: null,
      createdAt: new Date().toISOString(),
      userId: user.id,
      isOptimistic: true
    };

    setOptimisticMessages(prev => [...prev, optimisticUserMessage]);
    setMessage('');
    
    // Set uploading state if files are present
    if (selectedFiles.length > 0) {
      setUploadingFiles(true);
    }
    
    // When user sends message, stick to bottom and scroll immediately
    setIsStuckToBottom(true);
    setNewMessagesWhileScrolled(0);
    scrollToBottom(true);
    
    // Refocus textarea after sending message
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 50);
    
    try {
      devLog('Calling sendMessage with validated parameters:', messageParams);
      setUploadingFiles(selectedFiles.length > 0);
      const result = await sendMessage(messageParams);
      devLog('sendMessage result:', result);
      
      // Reset file upload state on successful send
      if (selectedFiles.length > 0) {
        resetFileUpload();
        setUploadingFiles(false);
      }
      
      // Schedule delayed refetch with improved logic
      const refetchWithRetry = async (attempt = 1) => {
        const maxRetries = 3;
        const baseDelay = 1000; // Start with 1 second delay
        
        try {
          // Wait before refetching to ensure database consistency
          await new Promise(resolve => {
            refetchTimeoutRef.current = setTimeout(resolve, baseDelay * attempt);
          });
          
          const refetchResult = await refetchMessages();
          
          // Only clear optimistic messages if we can confirm real messages have arrived
          // Check if there are more real messages than before, indicating our message was processed
          if (refetchResult && Array.isArray(refetchResult)) {
            // Look for a message with matching content that's not optimistic
            const hasMatchingRealMessage = refetchResult.some(msg => 
              !msg.isOptimistic && 
              msg.userMessage && 
              renderMessageContent(msg.userMessage) === userMessage &&
              msg.userId === user.id
            );
            
            if (hasMatchingRealMessage) {
              // Only remove this specific optimistic message
              setOptimisticMessages(prev => 
                prev.filter(msg => msg.id !== tempMessageId)
              );
              setRetryCount(0);
              
              // Refocus textarea when optimistic message is cleared
              setTimeout(() => {
                textareaRef.current?.focus();
              }, 100);
            } else if (attempt < maxRetries) {
              // Real message not found yet, retry
              setRetryCount(attempt);
              refetchWithRetry(attempt + 1);
            } else {
              // After max retries, keep optimistic message but stop retrying
              setRetryCount(0);
              devWarn('Real message not confirmed after all retries, keeping optimistic message');
              
              // Refocus textarea even if retries failed
              setTimeout(() => {
                textareaRef.current?.focus();
              }, 100);
            }
          } else if (attempt < maxRetries) {
            setRetryCount(attempt);
            refetchWithRetry(attempt + 1);
          } else {
            setRetryCount(0);
            devWarn('Refetch returned unexpected result, keeping optimistic message');
            
            // Refocus textarea
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 100);
          }
          
        } catch (refetchError) {
          devWarn(`Refetch attempt ${attempt} failed:`, refetchError);
          
          if (attempt < maxRetries) {
            setRetryCount(attempt);
            // Exponential backoff for retries
            refetchWithRetry(attempt + 1);
          } else {
            // After max retries, keep the optimistic message
            setRetryCount(0);
            devError('Failed to refetch messages after all retries, keeping optimistic message:', refetchError);
            
            // Refocus textarea after failed retries
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 100);
          }
        }
      };
      
      refetchWithRetry();
      
    } catch (error) {
      devError('Error sending message:', error);
      
      // On send error, remove optimistic message and restore input
      setOptimisticMessages(prev => 
        prev.filter(msg => msg.id !== tempMessageId)
      );
      setMessage(userMessage); // Restore the message so user can retry
      
      // Reset file upload states on error
      if (selectedFiles.length > 0) {
        setUploadingFiles(false);
      }
      
      // Refocus textarea when there's an error
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      
      // Show more specific error message to user
      let errorMessage = 'Failed to send message. Please try again.';
      
      if (error?.message) {
        if (error.message.includes('thread') || error.message.includes('Thread')) {
          errorMessage = 'Error: Invalid conversation thread. Please try refreshing the page.';
        } else if (error.message.includes('assistant') || error.message.includes('Assistant')) {
          errorMessage = 'Error: Assistant not available. Please try selecting a different assistant.';
        } else if (error.message.includes('user') || error.message.includes('User')) {
          errorMessage = 'Error: User authentication issue. Please try signing in again.';
        } else if (error.message.includes('permission') || error.message.includes('access')) {
          errorMessage = 'Error: Access denied. You may not have permission to use this assistant.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      devLog('Showing error to user:', errorMessage);
      // Error is also handled by the global action's error state, but we can show immediate feedback
    }
  }, [message, sendingMessage, user?.id, assistantId, threadReady, currentThreadId, sendMessage, refetchMessages, optimisticMessages, selectedFiles, resetFileUpload, renderMessageContent]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderAssistantMessage = (content) => {
    const messageText = renderMessageContent(content);
    try {
      const html = marked.parse(messageText);
      return <div dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (error) {
      return <div>{messageText}</div>;
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Flag Message Modal Component
  const FlagMessageModal = () => {
    if (!showFlagModal || !flaggingMessageId) return null;

    const flagOptions = [
      { value: '', label: 'Select a reason...' },
      { value: 'inappropriate_content', label: 'Inappropriate Content' },
      { value: 'incorrect_information', label: 'Incorrect Medical Information' },
      { value: 'poor_quality', label: 'Poor Quality Response' },
      { value: 'off_topic', label: 'Off-Topic Response' },
      { value: 'unsafe_advice', label: 'Potentially Unsafe Medical Advice' },
      { value: 'bias_discrimination', label: 'Bias or Discrimination' },
      { value: 'privacy_concern', label: 'Privacy Concern' },
      { value: 'other', label: 'Other' }
    ];

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
          border: isDarkMode ? '1px solid #4a5568' : '1px solid #e2e8f0'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>🚩</div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '8px',
              color: isDarkMode ? '#ffffff' : '#1a202c'
            }}>
              Flag Message
            </h2>
            <p style={{
              color: isDarkMode ? '#a0aec0' : '#4a5568',
              fontSize: '16px'
            }}>
              Help us improve by reporting problematic content
            </p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: isDarkMode ? '#f3f4f6' : '#1f2937'
            }}>
              Reason for flagging *
            </label>
            <select
              value={flagReasons[flaggingMessageId] || ''}
              onChange={(e) => handleFlagReasonChange(flaggingMessageId, e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: `2px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '8px',
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#f3f4f6' : '#1f2937',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4b5563' : '#d1d5db';
              }}
            >
              {flagOptions.map(option => (
                <option key={option.value} value={option.value} disabled={option.value === ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: isDarkMode ? '#f3f4f6' : '#1f2937'
            }}>
              Additional comments (optional)
            </label>
            <textarea
              value={flagComments[flaggingMessageId] || ''}
              onChange={(e) => handleFlagCommentChange(flaggingMessageId, e.target.value)}
              placeholder="Provide additional details about why you're flagging this message..."
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '12px',
                border: `2px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '8px',
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#f3f4f6' : '#1f2937',
                fontSize: '14px',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4b5563' : '#d1d5db';
              }}
            />
          </div>

          <div style={{
            padding: '16px',
            backgroundColor: isDarkMode ? '#374151' : '#f9fafb',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <div style={{
              fontSize: '12px',
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              lineHeight: '1.4'
            }}>
              <strong>Note:</strong> Flagged messages will be reviewed by our team to improve the AI assistant's responses. 
              Your feedback helps ensure the quality and safety of medical information provided.
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={handleCancelFlag}
              style={{
                padding: '12px 20px',
                backgroundColor: 'transparent',
                border: `2px solid ${isDarkMode ? '#6b7280' : '#d1d5db'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                color: isDarkMode ? '#cbd5e0' : '#4a5568',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = isDarkMode ? '#374151' : '#f9fafb';
                e.target.style.borderColor = isDarkMode ? '#9ca3af' : '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = isDarkMode ? '#6b7280' : '#d1d5db';
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={handleSaveFlag}
              disabled={savingFlags[flaggingMessageId] || !flagReasons[flaggingMessageId]}
              style={{
                padding: '12px 20px',
                backgroundColor: (!flagReasons[flaggingMessageId] || savingFlags[flaggingMessageId]) 
                  ? (isDarkMode ? '#374151' : '#9ca3af') 
                  : '#dc2626',
                border: 'none',
                borderRadius: '8px',
                cursor: (!flagReasons[flaggingMessageId] || savingFlags[flaggingMessageId]) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                opacity: (!flagReasons[flaggingMessageId] || savingFlags[flaggingMessageId]) ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (flagReasons[flaggingMessageId] && !savingFlags[flaggingMessageId]) {
                  e.target.style.backgroundColor = '#b91c1c';
                }
              }}
              onMouseLeave={(e) => {
                if (flagReasons[flaggingMessageId] && !savingFlags[flaggingMessageId]) {
                  e.target.style.backgroundColor = '#dc2626';
                }
              }}
            >
              {savingFlags[flaggingMessageId] ? (
                <>
                  <div className="spinner" style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Flagging...
                </>
              ) : (
                <>
                  🚩 Flag Message
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Thread Selection Modal Component
  const ThreadSelectionModal = () => {
    if (!showThreadModal) return null;

    // Group threads by recency
    const groupedThreads = groupThreadsByRecency(threads);
    const [expandedGroups, setExpandedGroups] = useState({
      today: true,
      thisWeek: true,
      thisMonth: false,
      older: false
    });

    // Generate summaries for visible threads when modal opens
    useEffect(() => {
      if (showThreadModal && threads) {
        // Skip summary generation entirely if there are more than 10 threads to avoid bulk processing
        if (threads.length > 10) {
          return;
        }
        
        const allThreads = Object.values(groupedThreads).flat();
        const threadsNeedingSummary = allThreads.filter(thread => {
          const messageCount = thread.messages?.edges?.length || 0;
          return messageCount > 3 && !threadSummaries[thread.id] && !loadingSummaries[thread.id];
        });
        
        // Limit to only the first 5 most recent threads to reduce API load
        const limitedThreads = threadsNeedingSummary
          .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
          .slice(0, 5);
        
        // Generate summaries with a small delay between each to avoid overwhelming the API
        limitedThreads.forEach((thread, index) => {
          setTimeout(() => {
            generateThreadSummaryAsync(thread);
          }, index * 500); // Increased delay to 500ms between each request
        });
      }
    }, [showThreadModal, threads, groupedThreads, threadSummaries, loadingSummaries, generateThreadSummaryAsync]);

    const toggleGroup = (groupKey) => {
      setExpandedGroups(prev => ({
        ...prev,
        [groupKey]: !prev[groupKey]
      }));
    };

    const getGroupTitle = (groupKey) => {
      const counts = {
        today: groupedThreads.today?.length || 0,
        thisWeek: groupedThreads.thisWeek?.length || 0,
        thisMonth: groupedThreads.thisMonth?.length || 0,
        older: groupedThreads.older?.length || 0
      };

      const titles = {
        today: `Today (${counts.today})`,
        thisWeek: `This Week (${counts.thisWeek})`,
        thisMonth: `This Month (${counts.thisMonth})`,
        older: `Older (${counts.older})`
      };

      return titles[groupKey] || groupKey;
    };

    const renderThreadGroup = (groupKey, threadList) => {
      if (!threadList || threadList.length === 0) return null;

      return (
        <div key={groupKey} style={{ marginBottom: '16px' }}>
          <button
            onClick={() => toggleGroup(groupKey)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: isDarkMode ? '#ffffff' : '#374151',
              marginBottom: '8px'
            }}
          >
            <span>{getGroupTitle(groupKey)}</span>
            <span style={{ fontSize: '16px' }}>
              {expandedGroups[groupKey] ? '▼' : '▶'}
            </span>
          </button>
          
          {expandedGroups[groupKey] && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {threadList.map(thread => (
                <div
                  key={thread.id}
                  onClick={() => {
                    setSelectedThreadForResume(thread.id);
                    handleResumeThread(thread.id);
                  }}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: selectedThreadForResume === thread.id 
                      ? (isDarkMode ? '#3b82f6' : '#dbeafe')
                      : (isDarkMode ? '#4a5568' : '#ffffff'),
                    border: '1px solid ' + (isDarkMode ? '#6b7280' : '#e5e7eb'),
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedThreadForResume !== thread.id) {
                      e.target.style.backgroundColor = isDarkMode ? '#374151' : '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedThreadForResume !== thread.id) {
                      e.target.style.backgroundColor = isDarkMode ? '#4a5568' : '#ffffff';
                    }
                  }}
                >
                  {/* AI Summary indicator */}
                  {threadSummaries[thread.id] && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      borderRadius: '50%',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '8px',
                      fontWeight: 'bold',
                      title: 'AI-generated summary'
                    }}>
                      ✨
                    </div>
                  )}
                  
                  {/* Loading indicator */}
                  {loadingSummaries[thread.id] && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid ' + (isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'),
                        borderTop: '2px solid ' + (isDarkMode ? '#ffffff' : '#374151'),
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    </div>
                  )}
                  
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '4px',
                    marginRight: '24px',
                    color: selectedThreadForResume === thread.id 
                      ? (isDarkMode ? '#ffffff' : '#1e40af')
                      : (isDarkMode ? '#ffffff' : '#111827'),
                    lineHeight: '1.3'
                  }}>
                    {getThreadDescriptionSync(thread)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: selectedThreadForResume === thread.id 
                      ? (isDarkMode ? '#cbd5e0' : '#3b82f6')
                      : (isDarkMode ? '#9ca3af' : '#6b7280')
                  }}>
                    {getThreadMessageCount(thread)} messages • {formatTimeAgo(getLastActivityTime(thread))}
                    {threadSummaries[thread.id] && (
                      <span style={{ marginLeft: '8px', fontSize: '10px', opacity: 0.8 }}>
                        • AI Summary
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
      }}>
        <div className="thread-modal" style={{
          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
          border: isDarkMode ? '1px solid #2d2d2d' : '1px solid #e2e8f0'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              marginBottom: '8px',
              color: isDarkMode ? '#ffffff' : '#1a202c'
            }}>
              Conversation Options
            </h2>
            <p style={{
              color: isDarkMode ? '#9ca3af' : '#6b7280',
              fontSize: '16px',
              lineHeight: '1.5'
            }}>
              Choose how you'd like to continue with {assistant?.name || 'this assistant'}
            </p>
          </div>

          {/* Info Box */}
          <div style={{
            padding: '16px',
            backgroundColor: isDarkMode ? '#1e3a8a' : '#dbeafe',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '1px solid ' + (isDarkMode ? '#3b82f6' : '#93c5fd')
          }}>
            <div style={{
              fontSize: '13px',
              color: isDarkMode ? '#bfdbfe' : '#1e40af',
              lineHeight: '1.4'
            }}>
              <strong>💡 Choose an option:</strong> Start a new conversation for a fresh topic, 
              or continue a previous conversation to build on your existing discussion. 
              Your conversation history helps me provide better, more contextual responses.
            </div>
          </div>

          {/* Start New Conversation Section */}
          <div style={{
            backgroundColor: isDarkMode ? '#374151' : '#f8fafc',
            padding: '24px',
            borderRadius: '12px',
            marginBottom: '24px',
            border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e2e8f0')
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                backgroundColor: '#10b981',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                +
              </div>
              <div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  margin: 0,
                  color: isDarkMode ? '#ffffff' : '#111827'
                }}>
                  Start Fresh
                </h3>
                <p style={{
                  fontSize: '14px',
                  margin: 0,
                  color: isDarkMode ? '#9ca3af' : '#6b7280'
                }}>
                  Begin a new conversation with a clean slate
                </p>
              </div>
            </div>
            
            <button
              onClick={handleStartNewThread}
              disabled={isCreatingThread}
              style={{
                width: '100%',
                padding: '14px 20px',
                backgroundColor: isCreatingThread ? (isDarkMode ? '#374151' : '#9ca3af') : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: isCreatingThread ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: isCreatingThread ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.2)'
              }}
              onMouseEnter={(e) => {
                if (!isCreatingThread) {
                  e.target.style.backgroundColor = '#059669';
                  e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isCreatingThread) {
                  e.target.style.backgroundColor = '#10b981';
                  e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.2)';
                }
              }}
            >
              {isCreatingThread ? (
                <>
                  <div className="spinner" style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Creating New Conversation...
                </>
              ) : (
                <>
                  ✨ Start New Conversation
                </>
              )}
            </button>
          </div>

          {/* Separator */}
          {threads && threads.length > 0 && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '24px',
                gap: '16px'
              }}>
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: isDarkMode ? '#4b5563' : '#d1d5db'
                }} />
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  whiteSpace: 'nowrap'
                }}>
                  Or Continue Previous
                </div>
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: isDarkMode ? '#4b5563' : '#d1d5db'
                }} />
              </div>



              {/* Existing Threads */}
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '2px'
              }}>
                {Object.entries(groupedThreads).map(([groupKey, threadList]) => 
                  renderThreadGroup(groupKey, threadList)
                )}
              </div>

              {/* Actions Row */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: '1px solid ' + (isDarkMode ? '#4b5563' : '#e5e7eb')
              }}>
                <button
                  onClick={handleCancelModal}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: 'transparent',
                    border: `2px solid ${isDarkMode ? '#6b7280' : '#d1d5db'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: isDarkMode ? '#cbd5e0' : '#4a5568',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#374151' : '#f9fafb';
                    e.target.style.borderColor = isDarkMode ? '#9ca3af' : '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = isDarkMode ? '#6b7280' : '#d1d5db';
                  }}
                >
                  Cancel
                </button>
                
                {selectedThreadForResume && (
                  <button
                    onClick={() => handleResumeThread()}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: '#3b82f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'white',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#3b82f6';
                    }}
                  >
                    Continue Selected
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Better error handling for assistant loading
  if (assistantError) {
    const errorMessage = assistantError.toString();
    const isAccessDenied = errorMessage.includes('Access denied') || errorMessage.includes('permission') || errorMessage.includes('unauthorized');
    const isNotFound = errorMessage.includes('not found') || errorMessage.includes('404');

    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
        color: isDarkMode ? '#ffffff' : '#000000',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          padding: '24px',
          backgroundColor: isDarkMode ? '#dc2626' : '#fee2e2',
          border: '1px solid ' + (isDarkMode ? '#b91c1c' : '#fecaca'),
          borderRadius: '12px',
          color: isDarkMode ? '#ffffff' : '#dc2626',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>
            {isAccessDenied ? '512' : isNotFound ? '615' : '6A0 '}
          </div>
          <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '18px' }}>
            {isAccessDenied 
              ? 'Access Denied' 
              : isNotFound 
                ? 'Assistant Not Found' 
                : 'Loading Error'
            }
          </div>
          <div style={{ marginBottom: '16px' }}>
            {isAccessDenied 
              ? 'You don\'t have permission to access this assistant. Please contact your administrator for access.'
              : isNotFound 
                ? `No assistant found with ID: ${assistantId}`
                : `Error loading assistant: ${errorMessage}`
            }
          </div>
          <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '16px' }}>
            Assistant ID: {assistantId}<br/>
            User: {user?.email || 'Not authenticated'}<br/>
            Session: {session?.id || 'No session'}
          </div>
          <button
            onClick={() => onClose ? onClose() : navigate('/')}
            style={{
              padding: '8px 16px',
              backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              color: isDarkMode ? '#ffffff' : '#374151'
            }}
          >
            14 Go Back
          </button>
        </div>
      </div>
    );
  }

  // Enhanced loading state with more details and timeout handling
  if ((!assistant && assistantFetching) || loadingTimeout) {
    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
        color: isDarkMode ? '#ffffff' : '#000000',
        position: 'relative'
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px'
        }}>
          {loadingTimeout ? (
            <div style={{
              textAlign: 'center',
              backgroundColor: isDarkMode ? '#dc2626' : '#fee2e2',
              color: isDarkMode ? '#ffffff' : '#dc2626',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '500px'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏰</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
                Loading Timeout
              </div>
              <div style={{ marginBottom: '16px' }}>
                The assistant is taking too long to load. This might indicate a connection issue or the assistant may not exist.
              </div>
              <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '16px' }}>
                Assistant ID: {assistantId}<br/>
                User: {user?.email || 'Not authenticated'}<br/>
                Time waited: 15+ seconds
              </div>
              <button
                onClick={() => {
                  setLoadingTimeout(false);
                  window.location.reload();
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: isDarkMode ? '#ffffff' : '#374151',
                  marginRight: '8px'
                }}
              >
                12 Retry
              </button>
              <button
                onClick={() => onClose ? onClose() : navigate('/')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: isDarkMode ? '#ffffff' : '#374151'
                }}
              >
                14 Go Back
              </button>
            </div>
          ) : (
            <>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '4px solid ' + (isDarkMode ? '#374151' : '#e5e7eb'),
                borderTop: '4px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                  Loading Assistant...
                </div>
                <div style={{ fontSize: '14px', opacity: 0.7 }}>
                  Assistant ID: {assistantId}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.5, marginTop: '4px' }}>
                  User: {user?.email || 'Loading...'}<br/>
                  Session: {session?.id || 'Loading...'}
                </div>
                {assistantFetching && (
                  <div style={{ fontSize: '11px', opacity: 0.4, marginTop: '8px' }}>
                    Fetching assistant data...
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Handle case where assistant failed to load but no error was thrown
  if (!assistant && !assistantFetching) {
    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
        color: isDarkMode ? '#ffffff' : '#000000',
        position: 'relative'
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            padding: '24px',
            backgroundColor: isDarkMode ? '#fbbf24' : '#fef3c7',
            border: '1px solid ' + (isDarkMode ? '#f59e0b' : '#fcd34d'),
            borderRadius: '12px',
            color: isDarkMode ? '#000000' : '#92400e',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '16px' }}>⚠️</div>
            <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '18px' }}>
              Assistant Not Available
            </div>
            <div style={{ marginBottom: '16px' }}>
              The assistant could not be loaded. This might be due to access restrictions or the assistant not existing.
            </div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '16px' }}>
              Assistant ID: {assistantId}<br/>
              User: {user?.email || 'Not authenticated'}<br/>
              Session: {session?.id || 'No session'}<br/>
              Loading completed without data
            </div>
            <button
              onClick={() => onClose ? onClose() : navigate('/')}
              style={{
                padding: '8px 16px',
                backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: isDarkMode ? '#ffffff' : '#374151'
              }}
            >
              14 Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      maxHeight: '100vh',
      backgroundColor: isDarkMode ? '#111111' : '#ffffff',
      color: isDarkMode ? '#ffffff' : '#000000',
      overflow: 'hidden'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes bounce {
            0%, 80%, 100% {
              transform: scale(0);
            }
            40% {
              transform: scale(1);
            }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .message-content p {
            margin: 0.5em 0;
          }
          
          .message-content ul, .message-content ol {
            padding-left: 1.5em;
            margin: 0.5em 0;
          }
          
          .message-content li {
            margin: 0.25em 0;
          }
          
          .message-content h1, .message-content h2, .message-content h3, .message-content h4, .message-content h5, .message-content h6 {
            margin: 1em 0 0.5em 0;
            font-weight: bold;
          }
          
          .message-content code {
            background-color: ${isDarkMode ? '#2d3748' : '#f3f4f6'};
            color: ${isDarkMode ? '#e2e8f0' : '#1a202c'};
            padding: 0.125em 0.25em;
            border-radius: 0.25em;
          }
          
          .message-content pre {
            background-color: ${isDarkMode ? '#2d3748' : '#f3f4f6'};
            color: ${isDarkMode ? '#e2e8f0' : '#1a202c'};
            padding: 0.75em;
            border-radius: 0.5em;
            overflow: auto;
          }
          
          .scrollarea {
            scrollbar-width: thin;
            scrollbar-color: ${isDarkMode ? '#4a5568 #2d3748' : '#6b7280 #f3f4f6'};
          }
          
          .scrollarea::-webkit-scrollbar {
            width: ${isMobile ? '3px' : '6px'};
          }
          
          .scrollarea::-webkit-scrollbar-track {
            background: ${isDarkMode ? '#2d3748' : '#f3f4f6'};
          }
          
          .scrollarea::-webkit-scrollbar-thumb {
            background: ${isDarkMode ? '#4a5568' : '#6b7280'};
            border-radius: 3px;
          }
          
          .scrollarea::-webkit-scrollbar-thumb:hover {
            background: ${isDarkMode ? '#718096' : '#4b5563'};
          }
          
          /* Ensure smooth scrolling */
          html {
            scroll-behavior: smooth;
          }
          
          .bounce-dot {
            width: 6px;
            height: 6px;
            background-color: ${isDarkMode ? '#a0aec0' : '#6b7280'};
            border-radius: 50%;
            display: inline-block;
            margin: 0 1px;
          }
          
          .bounce-dot:nth-child(1) { animation: bounce 1.4s ease-in-out infinite; }
          .bounce-dot:nth-child(2) { animation: bounce 1.4s ease-in-out infinite 0.2s; }
          .bounce-dot:nth-child(3) { animation: bounce 1.4s ease-in-out infinite 0.4s; }
          
          .flag-button {
            opacity: 0.6;
            transition: all 0.2s ease;
            background: none;
            border: none;
            cursor: pointer;
            padding: ${isMobile ? '8px' : '6px'};
            border-radius: 6px;
            font-size: ${isMobile ? '16px' : '14px'};
          }
          
          .flag-button:hover:not(:disabled) {
            opacity: 1;
            background-color: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} !important;
            transform: scale(1.1);
          }
          
          .message-bubble:hover .flag-button {
            opacity: 1;
          }
          
          .flag-button:disabled {
            cursor: not-allowed;
          }
          
          .flagged-message {
            border-left: 4px solid #dc2626;
            background-color: ${isDarkMode ? 'rgba(220, 38, 38, 0.1)' : 'rgba(220, 38, 38, 0.05)'};
            box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
          }
          
          .scroll-to-bottom {
            position: fixed;
            bottom: ${isMobile ? '120px' : '130px'};
            right: ${isMobile ? '16px' : '24px'};
            background-color: #3b82f6;
            color: white;
            border: none;
            border-radius: 50%;
            width: ${isMobile ? '48px' : '40px'};
            height: ${isMobile ? '48px' : '40px'};
            cursor: pointer;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: ${isMobile ? '20px' : '16px'};
            transition: all 0.2s ease;
          }
          
          .scroll-to-bottom:hover {
            background-color: #2563eb;
            transform: scale(1.1);
          }

          .jump-to-bottom {
            position: fixed;
            bottom: ${isMobile ? '120px' : '130px'};
            right: ${isMobile ? '16px' : '24px'};
            background-color: #3b82f6;
            color: white;
            border: none;
            border-radius: 50%;
            width: ${isMobile ? '56px' : '48px'};
            height: ${isMobile ? '56px' : '48px'};
            cursor: pointer;
            box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: ${isMobile ? '20px' : '18px'};
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            transform: translateY(0);
            animation: jumpInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .jump-to-bottom:hover {
            background-color: #2563eb;
            transform: translateY(-2px) scale(1.05);
            box-shadow: 0 8px 24px rgba(59, 130, 246, 0.5);
          }

          .jump-to-bottom.new-message {
            animation: pulseNewMessage 1s ease-in-out infinite;
          }

          .jump-to-bottom .message-badge {
            position: absolute;
            top: -8px;
            right: -8px;
            background-color: #dc2626;
            color: white;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: bold;
            box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4);
            animation: bounceIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          }

          @keyframes jumpInUp {
            0% {
              opacity: 0;
              transform: translateY(30px) scale(0.8);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes pulseNewMessage {
            0%, 100% {
              transform: translateY(0) scale(1);
              box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
            }
            50% {
              transform: translateY(-2px) scale(1.02);
              box-shadow: 0 8px 24px rgba(59, 130, 246, 0.6);
            }
          }

          @keyframes bounceIn {
            0% {
              opacity: 0;
              transform: scale(0.3);
            }
            50% {
              opacity: 1;
              transform: scale(1.1);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          .jump-to-bottom-exit {
            animation: jumpOutDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }

          @keyframes jumpOutDown {
            0% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            100% {
              opacity: 0;
              transform: translateY(30px) scale(0.8);
            }
          }
          
          /* Print styles */
          @media print {
            body.printing-chat * {
              visibility: hidden;
            }
            
            body.printing-chat .messages-area,
            body.printing-chat .messages-area * {
              visibility: visible;
            }
            
            body.printing-chat .messages-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              height: auto;
              background: white !important;
              color: black !important;
            }
            
            body.printing-chat .message-bubble {
              background: #f5f5f5 !important;
              color: black !important;
              border: 1px solid #ddd !important;
              page-break-inside: avoid;
              margin-bottom: 10px;
            }
            
            body.printing-chat .user-message {
              background: #e3f2fd !important;
              color: black !important;
              text-align: right;
            }
            
            body.printing-chat .flag-button,
            body.printing-chat .copy-button {
              display: none !important;
            }
          }
          
          .copy-button {
            opacity: 0.6;
            transition: all 0.2s ease;
            background: none;
            border: none;
            cursor: pointer;
            padding: ${isMobile ? '6px' : '4px'};
            border-radius: 4px;
            font-size: ${isMobile ? '14px' : '12px'};
            color: ${isDarkMode ? '#cbd5e0' : '#6b7280'};
          }
          
          .copy-button:hover:not(:disabled) {
            opacity: 1;
            background-color: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
            transform: scale(1.1);
          }
          
          .message-bubble:hover .copy-button {
            opacity: 1;
          }

          /* Mobile responsive styles */
          @media (max-width: 768px) {
            .message-bubble {
              max-width: 85% !important;
            }
            
            .thread-modal {
              margin: 10px !important;
              max-height: 90vh !important;
            }
          }
        `}
      </style>

      {/* Header */}
      <div style={{
        backgroundColor: isDarkMode ? '#1e1e1e' : '#f9fafb',
        padding: isMobile ? '12px 16px' : '16px 24px',
        borderBottom: '1px solid ' + (isDarkMode ? '#2d2d2d' : '#e5e7eb'),
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '8px' : '16px',
          flex: 1 }}>
          <button
            onClick={() => onClose ? onClose() : navigate('/')}
            style={{
              padding: isMobile ? '6px 8px' : '8px 12px',
              backgroundColor: isDarkMode ? '#2d2d2d' : '#f3f4f6',
              border: '1px solid ' + (isDarkMode ? '#404040' : '#d1d5db'),
              borderRadius: '8px',
              cursor: 'pointer',
              color: isDarkMode ? '#ffffff' : '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#404040' : '#e5e7eb';
              e.target.style.borderColor = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#2d2d2d' : '#f3f4f6';
              e.target.style.borderColor = isDarkMode ? '#404040' : '#d1d5db';
            }}
          >
            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>←</span>
            {!isMobile && 'Back to Assistant Select'}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flex: 1 }}>
            <div style={{
              width: isMobile ? '28px' : '32px',
              height: isMobile ? '28px' : '32px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: 'bold'
            }}>
              {assistant?.name?.charAt(0)?.toUpperCase() || 'M'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontWeight: 'bold',
                fontSize: isMobile ? '16px' : '18px',
                color: isDarkMode ? '#ffffff' : '#111827',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {assistant?.name || 'Loading...'}
              </div>
              {!isMobile && (
                <div style={{
                  fontSize: '14px',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {assistant?.name === 'Maia' ? 'Medical AI Assistant' : (assistant?.description || 'AI Assistant')}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={toggleTheme}
            style={{
              padding: isMobile ? '6px 8px' : '8px 12px',
              backgroundColor: isDarkMode ? '#2d2d2d' : '#f3f4f6',
              border: '1px solid ' + (isDarkMode ? '#404040' : '#d1d5db'),
              borderRadius: '8px',
              cursor: 'pointer',
              color: isDarkMode ? '#ffffff' : '#000000',
              fontSize: isMobile ? '12px' : '14px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#404040' : '#e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = isDarkMode ? '#2d2d2d' : '#f3f4f6';
            }}
          >
            {isDarkMode ? (isMobile ? '☀️' : '☀️ Light') : (isMobile ? '🌙' : '🌙 Dark')}
          </button>
          
          {/* Print/Save Controls */}
          {printSaveAvailable && (
            <>
              <button
                onClick={handlePrintChat}
                disabled={isPrinting}
                style={{
                  padding: isMobile ? '6px 8px' : '8px 12px',
                  backgroundColor: isDarkMode ? '#2d2d2d' : '#f3f4f6',
                  border: '1px solid ' + (isDarkMode ? '#404040' : '#d1d5db'),
                  borderRadius: '8px',
                  cursor: isPrinting ? 'not-allowed' : 'pointer',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  fontSize: isMobile ? '12px' : '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: isPrinting ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                title="Print chat"
                onMouseEnter={(e) => {
                  if (!isPrinting) {
                    e.target.style.backgroundColor = isDarkMode ? '#404040' : '#e5e7eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isPrinting) {
                    e.target.style.backgroundColor = isDarkMode ? '#2d2d2d' : '#f3f4f6';
                  }
                }}
              >
                {isPrinting ? (
                  <>
                    <div className="spinner" style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid rgba(0,0,0,0.3)',
                      borderTop: '2px solid currentColor',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {!isMobile && 'Printing...'}
                  </>
                ) : (
                  <>
                    🖨️
                    {!isMobile && ' Print'}
                  </>
                )}
              </button>

              <button
                onClick={handleSaveChatAsPDF}
                disabled={isSavingPDF}
                style={{
                  padding: isMobile ? '6px 8px' : '8px 12px',
                  backgroundColor: isDarkMode ? '#2d2d2d' : '#f3f4f6',
                  border: '1px solid ' + (isDarkMode ? '#404040' : '#d1d5db'),
                  borderRadius: '8px',
                  cursor: isSavingPDF ? 'not-allowed' : 'pointer',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  fontSize: isMobile ? '12px' : '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: isSavingPDF ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                title="Save chat as PDF"
                onMouseEnter={(e) => {
                  if (!isSavingPDF) {
                    e.target.style.backgroundColor = isDarkMode ? '#404040' : '#e5e7eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSavingPDF) {
                    e.target.style.backgroundColor = isDarkMode ? '#2d2d2d' : '#f3f4f6';
                  }
                }}
              >
                {isSavingPDF ? (
                  <>
                    <div className="spinner" style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid rgba(0,0,0,0.3)',
                      borderTop: '2px solid currentColor',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {!isMobile && 'Saving...'}
                  </>
                ) : (
                  <>
                    💾
                    {!isMobile && ' PDF'}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages Area */}
        <div 
          className="messages-area"
          ref={chatContainerRef}
          style={{ 
            flex: 1, 
            overflow: 'hidden', 
            position: 'relative',
            minHeight: 0 // Ensures flex child can shrink below content size
          }}
        >
          <div
            ref={messagesContainerRef}
            className="scrollarea"
            style={{
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '24px 16px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {messagesError && (
              <div style={{
                padding: '16px',
                backgroundColor: isDarkMode ? '#dc2626' : '#fee2e2',
                border: '1px solid ' + (isDarkMode ? '#b91c1c' : '#fecaca'),
                borderRadius: '8px',
                color: isDarkMode ? '#ffffff' : '#dc2626',
                marginBottom: '16px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  77 

620  Error Loading Messages
                </div>
                <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                  {messagesError.toString()}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Thread ID: {currentThreadId || 'None'}
                </div>
              </div>
            )}

            {messagesFetching && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center', 
                padding: '32px',
                gap: '12px'
              }}>
                <div className="spinner" style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid ' + (isDarkMode ? '#374151' : '#e5e7eb'),
                  borderTop: '3px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{ 
                  fontSize: '14px', 
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  textAlign: 'center'
                }}>
                  <div>Loading messages...</div>
                  <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                    Thread: {currentThreadId || 'Initializing...'}
                  </div>
                </div>
              </div>
            )}

            {/* Greeting message when thread is ready but no user messages yet (excluding init message) */}
            {threadReady && (!messages || messages.filter(msg => {
              const messageContent = renderMessageContent(msg.userMessage);
              return msg.userMessage && messageContent !== "Hello, I'm an EMS student seeking medical guidance. How can you help me?";
            }).length === 0) && !messagesFetching && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                textAlign: 'center',
                minHeight: '400px'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  marginBottom: '16px'
                }}>
                  {assistant?.name?.charAt(0)?.toUpperCase() || 'M'}
                </div>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  color: isDarkMode ? '#ffffff' : '#111827'
                }}>
                  Welcome to {assistant?.name || 'AI Assistant'}
                </h2>
                <p style={{
                  maxWidth: '400px',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  lineHeight: '1.5',
                  marginBottom: '16px'
                }}>
                  {greeting || "I'm here to help you with medical questions and information. Start a conversation by typing your question below."}
                </p>
                {/* Optional greeting message from assistant */}
                {greeting && (
                  <div style={{
                    maxWidth: '500px',
                    backgroundColor: isDarkMode ? '#4a5568' : '#f3f4f6',
                    color: isDarkMode ? '#f7fafc' : '#111827',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}>
                    <div className="message-content">
                      {renderAssistantMessage(greeting)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show thread preparation message when not ready */}
            {!threadReady && isCreatingThread && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                textAlign: 'center',
                minHeight: '200px',
                gap: '16px'
              }}>
                <div className="spinner" style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid ' + (isDarkMode ? '#374151' : '#e5e7eb'),
                  borderTop: '3px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: isDarkMode ? '#ffffff' : '#111827',
                  marginBottom: '8px'
                }}>
                  Preparing Your Conversation
                </div>
                <p style={{
                  maxWidth: '400px',
                  color: isDarkMode ? '#9ca3af' : '#6b7280',
                  lineHeight: '1.5'
                }}>
                  Setting up your medical assistant conversation thread...
                </p>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '16px',
              flex: 1,
              minHeight: 'min-content'
            }}>
              {/* Render messages */}
              {[...(messages || []), ...optimisticMessages]
                .filter((msg, index, array) => {
                  // Filter out the initialization message
                  if (msg.userMessage) {
                    const messageContent = renderMessageContent(msg.userMessage);
                    if (messageContent === "Hello, I'm an EMS student seeking medical guidance. How can you help me?") {
                      return false;
                    }
                  }

                  // Remove duplicates: if this is a real message, filter out any optimistic message with same content
                  if (!msg.isOptimistic && msg.userMessage) {
                    const messageContent = renderMessageContent(msg.userMessage);
                    // Check if there's an optimistic message with the same content that should be filtered out
                    const hasOptimisticDuplicate = array.some((otherMsg, otherIndex) => 
                      otherIndex !== index &&
                      otherMsg.isOptimistic && 
                      otherMsg.userMessage &&
                      renderMessageContent(otherMsg.userMessage) === messageContent &&
                      otherMsg.userId === msg.userId
                    );
                    // Always keep real messages
                    return true;
                  }
                  
                  // For optimistic messages, only keep if there's no matching real message
                  if (msg.isOptimistic && msg.userMessage) {
                    const messageContent = renderMessageContent(msg.userMessage);
                    const hasRealDuplicate = array.some((otherMsg, otherIndex) => 
                      otherIndex !== index &&
                      !otherMsg.isOptimistic && 
                      otherMsg.userMessage &&
                      renderMessageContent(otherMsg.userMessage) === messageContent &&
                      otherMsg.userId === msg.userId
                    );
                    return !hasRealDuplicate;
                  }
                  
                  // Keep all assistant messages and other message types
                  return true;
                })
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map((msg) => (
                <div key={msg.id}>
                  {/* User Message */}
                  {msg.userMessage && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                      <div style={{
                        maxWidth: '70%',
                        backgroundColor: msg.isOptimistic ? '#4a90e2' : '#0066cc',
                        color: 'white',
                        padding: '12px 16px',
                        borderRadius: '18px',
                        fontSize: '14px',
                        whiteSpace: 'pre-wrap',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        opacity: msg.isOptimistic ? 0.8 : 1,
                        position: 'relative'
                      }}>
                        {renderMessageContent(msg.userMessage)}
                        {msg.isOptimistic && (
                          <div style={{
                            position: 'absolute',
                            bottom: '-2px',
                            right: '8px',
                            fontSize: '10px',
                            opacity: 0.7
                          }}>
                            Sending...
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Assistant Message */}
                  {msg.assistantMessage && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        backgroundColor: '#2c5282',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        marginTop: '4px',
                        flexShrink: 0,
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}>
                        M
                      </div>
                      <div style={{ flex: 1, maxWidth: '70%' }}>
                        <div 
                          className={`message-bubble${msg.reviewStatus === 'flagged' ? ' flagged-message' : ''}`}
                          style={{
                            backgroundColor: isDarkMode ? '#1e1e1e' : '#f7fafc',
                            color: isDarkMode ? '#e2e8f0' : '#2d3748',
                            padding: '12px 16px',
                            borderRadius: '18px',
                            fontSize: '14px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            border: isDarkMode ? '1px solid #2d2d2d' : '1px solid #e2e8f0',
                            position: 'relative'
                          }}
                        >
                          <div className="message-content">
                            {renderAssistantMessage(msg.assistantMessage)}
                          </div>
                            
                          {/* Flag Status Indicator */}
                          {msg.reviewStatus === 'flagged' && (
                            <div style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                            }}>
                              🚩
                            </div>
                          )}
                            
                          {/* Action Buttons */}
                          <div style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            display: 'flex',
                            gap: '4px'
                          }}>
                            {/* Copy Button */}
                            <button
                              className="copy-button"
                              data-copy-id={msg.id}
                              onClick={() => handleCopyMessage(msg.id, msg.assistantMessage)}
                              disabled={copyingMessageId === msg.id}
                              title="Copy message"
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: copyingMessageId === msg.id ? 'not-allowed' : 'pointer',
                                padding: isMobile ? '6px' : '4px',
                                borderRadius: '4px',
                                fontSize: isMobile ? '14px' : '12px',
                                opacity: 0.6,
                                transition: 'all 0.2s ease',
                                color: isDarkMode ? '#cbd5e0' : '#6b7280'
                              }}
                            >
                              {copyingMessageId === msg.id ? '⏳' : '📋'}
                            </button>

                            {/* Flag Button */}
                            <button
                              className="flag-button"
                              onClick={() => handleFlagMessage(msg.id)}
                              disabled={msg.reviewStatus === 'flagged'}
                              title={msg.reviewStatus === 'flagged' ? 'Message already flagged for review' : 'Flag this message for review'}
                              style={{
                                background: msg.reviewStatus === 'flagged' 
                                  ? (isDarkMode ? 'rgba(220, 38, 38, 0.2)' : 'rgba(220, 38, 38, 0.1)')
                                  : 'none',
                                border: 'none',
                                cursor: msg.reviewStatus === 'flagged' ? 'not-allowed' : 'pointer',
                                padding: isMobile ? '8px' : '6px',
                                borderRadius: '6px',
                                fontSize: isMobile ? '16px' : '14px',
                                opacity: msg.reviewStatus === 'flagged' ? 0.8 : 0.6,
                                transition: 'all 0.2s ease',
                                color: msg.reviewStatus === 'flagged' ? '#dc2626' : (isDarkMode ? '#cbd5e0' : '#6b7280')
                              }}
                            >
                              🚩
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    marginTop: '4px',
                    flexShrink: 0
                  }}>
                    {assistant?.name?.charAt(0)?.toUpperCase() || 'M'}
                  </div>
                  <div style={{
                    backgroundColor: isDarkMode ? '#1e1e1e' : '#f3f4f6',
                    border: isDarkMode ? '1px solid #2d2d2d' : 'none',
                    padding: '12px 16px',
                    borderRadius: '18px'
                  }}>
                    <div className="bounce-dot"></div>
                    <div className="bounce-dot"></div>
                    <div className="bounce-dot"></div>
                  </div>
                </div>
              )}
            </div>

            <div ref={messagesEndRef} style={{ height: '1px', width: '100%' }} />
          </div>

          {/* Enhanced Jump to Bottom Button */}
          {showJumpToBottom && (
            <button
              className={`jump-to-bottom ${newMessageAnimation ? 'new-message' : ''}`}
              onClick={() => {
                setIsStuckToBottom(true);
                setNewMessagesWhileScrolled(0);
                scrollToBottom(true);
              }}
              aria-label={`Jump to bottom ${newMessagesWhileScrolled > 0 ? `(${Math.min(newMessagesWhileScrolled, 99)} new messages)` : ''}`}
              title={`Jump to bottom of conversation${newMessagesWhileScrolled > 0 ? ` - ${newMessagesWhileScrolled} new message${newMessagesWhileScrolled !== 1 ? 's' : ''}` : ''}`}
              style={{
                position: 'fixed',
                bottom: isMobile ? '120px' : '130px',
                right: isMobile ? '16px' : '24px',
                backgroundColor: newMessagesWhileScrolled > 0 ? '#dc2626' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: isMobile ? '56px' : '48px',
                height: isMobile ? '56px' : '48px',
                cursor: 'pointer',
                boxShadow: newMessagesWhileScrolled > 0 
                  ? '0 4px 16px rgba(220, 38, 38, 0.4)' 
                  : '0 4px 16px rgba(59, 130, 246, 0.4)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '20px' : '18px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontWeight: 'bold',
                animation: newMessageAnimation ? 'pulseNewMessage 1s ease-in-out infinite' : 'none'
              }}
              onMouseEnter={(e) => {
                const hoverColor = newMessagesWhileScrolled > 0 ? '#b91c1c' : '#2563eb';
                const hoverShadow = newMessagesWhileScrolled > 0 
                  ? '0 8px 24px rgba(220, 38, 38, 0.5)' 
                  : '0 8px 24px rgba(59, 130, 246, 0.5)';
                e.target.style.backgroundColor = hoverColor;
                e.target.style.transform = 'translateY(-2px) scale(1.05)';
                e.target.style.boxShadow = hoverShadow;
              }}
              onMouseLeave={(e) => {
                const normalColor = newMessagesWhileScrolled > 0 ? '#dc2626' : '#3b82f6';
                const normalShadow = newMessagesWhileScrolled > 0 
                  ? '0 4px 16px rgba(220, 38, 38, 0.4)' 
                  : '0 4px 16px rgba(59, 130, 246, 0.4)';
                e.target.style.backgroundColor = normalColor;
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = normalShadow;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsStuckToBottom(true);
                  setNewMessagesWhileScrolled(0);
                  scrollToBottom(true);
                }
              }}
            >
              ↓
              {newMessagesWhileScrolled > 0 && (
                <div 
                  className="message-badge"
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#ffffff',
                    color: '#dc2626',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    border: '2px solid #ffffff'
                  }}
                >
                  {Math.min(newMessagesWhileScrolled, 99)}
                </div>
              )}
            </button>
          )}
        </div>

        {/* Input Area */}
        <div 
          style={{
            backgroundColor: isDarkMode ? '#1e1e1e' : '#f9fafb',
            padding: '16px 24px',
            borderTop: '1px solid ' + (isDarkMode ? '#2d2d2d' : '#e5e7eb'),
            boxShadow: '0 -1px 3px 0 rgba(0, 0, 0, 0.1)',
            position: 'relative'
          }}
          onDragOver={canUploadFiles() ? handleDragOver : undefined}
          onDragEnter={canUploadFiles() ? handleDragEnter : undefined}
          onDragLeave={canUploadFiles() ? handleDragLeave : undefined}
          onDrop={canUploadFiles() ? handleDrop : undefined}
        >
          {/* Drag and Drop Overlay */}
          {dragActive && canUploadFiles() && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              border: '2px dashed #3b82f6',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              pointerEvents: 'none'
            }}>
              <div style={{
                textAlign: 'center',
                color: '#3b82f6',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                📁 Drop files here to upload
              </div>
            </div>
          )}
          {(sendError || initError) && (
            <div style={{
              padding: '12px',
              backgroundColor: isDarkMode ? '#dc2626' : '#fee2e2',
              border: '1px solid ' + (isDarkMode ? '#b91c1c' : '#fecaca'),
              borderRadius: '6px',
              color: isDarkMode ? '#ffffff' : '#dc2626',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
               Error {sendError ? 'sending message' : 'initializing thread'}: {(() => {
                const errorString = (sendError || initError).toString();
                if (errorString.includes('thread') || errorString.includes('Thread')) {
                  return 'Invalid conversation thread. Please try refreshing the page.';
                } else if (errorString.includes('assistant') || errorString.includes('Assistant')) {
                  return 'Assistant not available. Please try selecting a different assistant.';
                } else if (errorString.includes('user') || errorString.includes('User')) {
                  return 'User authentication issue. Please try signing in again.';
                } else if (errorString.includes('permission') || errorString.includes('access')) {
                  return 'Access denied. You may not have permission to use this assistant.';
                } else {
                  return errorString;
                }
              })()}
              {retryCount > 0 && (
                <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                  Retrying... (attempt {retryCount}/3)
                </div>
              )}
              <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>
                Thread ID: {currentThreadId || 'None'} | Assistant ID: {assistantId} | User ID: {user?.id || 'None'}
              </div>
            </div>
          )}

          {/* File Preview Area */}
          {showFilePreview && selectedFiles.length > 0 && (
            <>
              {/* File Upload Instructions */}
              {assistant?.fileUploadInstructions && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: isDarkMode ? '#374151' : '#f0f9ff',
                  border: '1px solid ' + (isDarkMode ? '#4b5563' : '#bfdbfe'),
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: isDarkMode ? '#cbd5e0' : '#1e40af'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>📄 File Upload Guidelines:</div>
                  <div dangerouslySetInnerHTML={{ __html: assistant.fileUploadInstructions.markdown || assistant.fileUploadInstructions }} />
                </div>
              )}
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: isDarkMode ? '#2d2d2d' : '#f8fafc',
                border: '1px solid ' + (isDarkMode ? '#404040' : '#e2e8f0'),
                borderRadius: '8px'
              }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '8px',
                color: isDarkMode ? '#ffffff' : '#1a202c'
              }}>
                📎 Selected Files ({selectedFiles.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {selectedFiles.map((file, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
                    border: '1px solid ' + (isDarkMode ? '#2d2d2d' : '#d1d5db'),
                    borderRadius: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      <span style={{ fontSize: '16px' }}>
                        {file.type.startsWith('image/') ? '🖼️' : 
                         file.type === 'application/pdf' ? '📄' : 
                         file.type.startsWith('text/') ? '📝' : '📁'}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: isDarkMode ? '#ffffff' : '#1a202c',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {file.name}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          color: isDarkMode ? '#cbd5e0' : '#6b7280'
                        }}>
                          {formatFileSize(file.size)} • {file.type}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeSelectedFile(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        color: '#dc2626',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = isDarkMode ? 'rgba(220, 38, 38, 0.1)' : 'rgba(220, 38, 38, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                      title="Remove file"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              </div>
            </>
          )}

          {/* File Upload Error */}
          {fileUploadError && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: isDarkMode ? '#dc2626' : '#fee2e2',
              border: '1px solid ' + (isDarkMode ? '#b91c1c' : '#fecaca'),
              borderRadius: '6px',
              color: isDarkMode ? '#ffffff' : '#dc2626',
              marginBottom: '16px',
              fontSize: '12px'
            }}>
              ❌ {fileUploadError}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
            {/* File Upload Button */}
            {canUploadFiles() && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <input
                  type="file"
                  multiple
                  accept={assistant?.acceptedFileTypes?.join(',') || 'image/*,application/pdf,text/*'}
                  onChange={(e) => {
                    if (e.target.files?.length > 0) {
                      handleFileSelect(e.target.files);
                    }
                    e.target.value = ''; // Reset input
                  }}
                  style={{ display: 'none' }}
                  id="file-upload-input"
                />
                <label
                  htmlFor="file-upload-input"
                  style={{
                    padding: '8px 12px',
                    backgroundColor: isDarkMode ? '#2d2d2d' : '#f3f4f6',
                    border: '1px solid ' + (isDarkMode ? '#404040' : '#d1d5db'),
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: isDarkMode ? '#ffffff' : '#374151',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                    minWidth: isMobile ? '40px' : '60px',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#404040' : '#e5e7eb';
                    e.target.style.borderColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = isDarkMode ? '#2d2d2d' : '#f3f4f6';
                    e.target.style.borderColor = isDarkMode ? '#404040' : '#d1d5db';
                  }}
                  title={`Upload files (Max: ${formatFileSize(assistant?.maxFileSize || 10 * 1024 * 1024)})`}
                >
                  📎
                  {!isMobile && ' Files'}
                </label>
                {!isMobile && (
                  <div style={{
                    fontSize: '10px',
                    color: isDarkMode ? '#9ca3af' : '#6b7280',
                    textAlign: 'center',
                    maxWidth: '60px',
                    lineHeight: '1.2'
                  }}>
                    Max {formatFileSize(assistant?.maxFileSize || 10 * 1024 * 1024)}
                  </div>
                )}
              </div>
            )}
            
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={getPlaceholderText()}
              disabled={sendingMessage || initializingThread || !threadReady || uploadingFiles}
              style={{
                flex: 1,
                minHeight: '50px',
                maxHeight: '120px',
                padding: '12px 16px',
                backgroundColor: isDarkMode ? '#111111' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#111827',
                border: '1px solid ' + (isDarkMode ? '#2d2d2d' : '#d1d5db'),
                borderRadius: '12px',
                resize: 'none',
                outline: 'none',
                fontSize: '14px',
                lineHeight: '1.4',
                fontFamily: 'inherit',
                transition: 'border-color 0.2s ease',
                opacity: uploadingFiles ? 0.7 : 1
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = isDarkMode ? '#2d2d2d' : '#d1d5db';
              }}
            />
            
            <button
              onClick={handleSendMessage}
              disabled={(!message.trim() && selectedFiles.length === 0) || sendingMessage || initializingThread || !user?.id || !assistantId || !threadReady || uploadingFiles}
              style={{
                padding: '12px 20px',
                backgroundColor: ((!message.trim() && selectedFiles.length === 0) || sendingMessage || initializingThread || !user?.id || !assistantId || !threadReady || uploadingFiles) 
                  ? (isDarkMode ? '#374151' : '#9ca3af') 
                  : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: ((!message.trim() && selectedFiles.length === 0) || sendingMessage || initializingThread || !user?.id || !assistantId || !threadReady || uploadingFiles) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                minWidth: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: ((!message.trim() && selectedFiles.length === 0) || sendingMessage || initializingThread || !user?.id || !assistantId || !threadReady || uploadingFiles) 
                  ? 'none' 
                  : '0 2px 4px rgba(59, 130, 246, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!((!message.trim() && selectedFiles.length === 0) || sendingMessage || initializingThread || !user?.id || !assistantId || !threadReady || uploadingFiles)) {
                  e.target.style.backgroundColor = '#2563eb';
                  e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!((!message.trim() && selectedFiles.length === 0) || sendingMessage || initializingThread || !user?.id || !assistantId || !threadReady || uploadingFiles)) {
                  e.target.style.backgroundColor = '#3b82f6';
                  e.target.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                }
              }}
            >
              {(sendingMessage || initializingThread || uploadingFiles) ? (
                <>
                  <div className="spinner" style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {initializingThread ? 'Initializing...' : uploadingFiles ? 'Uploading...' : 'Sending...'}
                </>
              ) : (
                <>
                  <span style={{ fontSize: '16px' }}>↑</span>
                  {selectedFiles.length > 0 && !message.trim() ? 'Upload' : 'Send'}
                </>
              )}
            </button>
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: isDarkMode ? '#2d2d2d' : '#f3f4f6',
            border: '1px solid ' + (isDarkMode ? '#404040' : '#e2e8f0'),
            borderRadius: '8px',
            fontSize: '11px',
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            textAlign: 'center',
            lineHeight: '1.4'
          }}>
            <strong>Medical Disclaimer:</strong> This AI assistant provides general medical information for educational purposes only. 
            It is not a substitute for professional medical advice, diagnosis, or treatment. Always consult qualified healthcare 
            providers for medical concerns. In emergencies, contact emergency services immediately.
          </div>
        </div>

      <ThreadSelectionModal />
      <FlagMessageModal />
    </div>
  );
}